/**
 * BRE-5538: hold captures that happen before `posthog.init()` and replay them
 * once it lands, instead of dropping them silently.
 *
 * ## The bug
 *
 * `posthog.capture()` is a **no-op before init**. From posthog-js itself:
 *
 * ```ts
 * capture(event_name, properties, options) {
 *   if (!this.__loaded || !this.persistence || ...) {
 *     logger.uninitializedWarning('posthog.capture')
 *     return
 *   }
 * ```
 *
 * It logs to the console and returns. Nothing throws, so a caller wrapped in
 * try/catch — which `safeCapture` is — cannot tell a dropped event from a
 * delivered one.
 *
 * ## Why it hits the boot path specifically
 *
 * `posthog.init()` runs in `PostHogProvider`'s `useEffect`. React fires child
 * effects **before** parent effects, so anything a descendant captures during
 * mount races that init and loses.
 *
 * This has already bitten once: the `$pageview` on every hard load was
 * captured pre-init and silently dropped, which made hard-load-once-and-leave
 * users show zero pageviews (fixed in BRE-5496 by handing pageviews to
 * posthog-js's native `capture_pageview: "history_change"` — a fix that does
 * not generalise to our own events).
 *
 * ## Why the bias matters more than the loss
 *
 * The RxDB boot events resolve after several awaits, so whether they land
 * before or after init depends on **how long the boot takes**. Slow boots get
 * captured; fast boots get dropped.
 *
 * That is the worst possible direction for a performance metric: it censors
 * exactly the outcome the work is trying to produce. Measured on staging
 * 2026-08-07, five deliberate cold loads produced **zero**
 * `rxdb_init_pipeline_completed` events while the same session delivered 12
 * `layout_render_unblocked` and 45 `$identify`.
 *
 * ## What this does
 *
 * Queues pre-init captures with their real wall-clock time and replays them in
 * order once `markPostHogReady()` fires, passing the original timestamp
 * through so a replayed event lands at the moment it happened rather than the
 * moment it flushed. Without that, every buffered boot event would cluster at
 * init time and the durations would still be readable but the *sequence*
 * would be a lie.
 */

import { createLogger } from "@brev-io/common/src/utils/logger";

const logger = createLogger("Capture Buffer");

/**
 * Hard cap on buffered events.
 *
 * The window this covers is one page load's worth of pre-init captures, which
 * is single digits in practice. The cap exists so a page where init never runs
 * at all (blocked SDK, missing key, consent refusal) cannot grow the queue
 * without bound — not because we expect to approach it.
 */
export const MAX_BUFFERED_CAPTURES = 200;

export interface BufferedCapture {
  eventName: string;
  properties: Record<string, unknown>;
  /** Wall-clock at the CALL site, replayed as posthog's event timestamp. */
  capturedAt: Date;
}

type CaptureFn<Name extends string = string> = (
  eventName: Name,
  properties: Record<string, unknown>,
  options?: { timestamp?: Date },
) => void;

let ready = false;
let buffer: BufferedCapture[] = [];
let droppedFromOverflow = 0;
let warnedOverflow = false;

/**
 * Queue an event, or capture it now if PostHog is already up.
 *
 * `capture` is injected rather than imported so this module can be tested
 * without the SDK — the branch that decides whether an event survives is
 * exactly the branch a PostHog mock would erase.
 */
export function bufferOrCapture<Name extends string>(
  capture: CaptureFn<Name>,
  eventName: Name,
  properties: Record<string, unknown>,
  now: Date = new Date(),
): "captured" | "buffered" | "dropped" {
  if (ready) {
    capture(eventName, properties);
    return "captured";
  }

  if (buffer.length >= MAX_BUFFERED_CAPTURES) {
    droppedFromOverflow++;
    if (!warnedOverflow) {
      warnedOverflow = true;
      // Once, not per event: if init genuinely never runs, this fires on every
      // subsequent capture and the log itself becomes the problem.
      logger.warn(
        "Pre-init capture buffer is full; dropping events. PostHog init may never have run.",
        { cap: MAX_BUFFERED_CAPTURES },
      );
    }
    return "dropped";
  }

  buffer.push({ eventName, properties, capturedAt: now });
  return "buffered";
}

/**
 * Mark PostHog initialised and replay everything queued.
 *
 * Called from `PostHogProvider` immediately after `posthog.init()`. Idempotent:
 * a second call flushes nothing, because the buffer is emptied before replay.
 *
 * Replay order is insertion order — boot telemetry is a sequence, and events
 * arriving out of order would misrepresent which phase preceded which.
 */
export function markPostHogReady(capture: CaptureFn): {
  flushed: number;
  droppedFromOverflow: number;
} {
  ready = true;
  const pending = buffer;
  buffer = [];

  for (const item of pending) {
    try {
      // The original wall-clock, not the flush time. A replayed event that
      // lands at flush time would collapse the whole boot sequence onto one
      // instant and quietly invert phase ordering.
      capture(item.eventName, item.properties, { timestamp: item.capturedAt });
    } catch (err) {
      // One bad event must not strand the rest of the queue.
      logger.warn("Failed to replay buffered capture", {
        eventName: item.eventName,
        error: err instanceof Error ? err.message : String(err),
      });
    }
  }

  const overflow = droppedFromOverflow;
  if (pending.length > 0 || overflow > 0) {
    logger.info("Replayed pre-init captures", {
      flushed: pending.length,
      droppedFromOverflow: overflow,
    });
  }

  return { flushed: pending.length, droppedFromOverflow: overflow };
}

/** Test seam — production code never calls this. */
export function __resetCaptureBuffer(): void {
  ready = false;
  buffer = [];
  droppedFromOverflow = 0;
  warnedOverflow = false;
}

/** Test/diagnostic seam. */
export function __captureBufferState(): {
  ready: boolean;
  size: number;
  droppedFromOverflow: number;
} {
  return { ready, size: buffer.length, droppedFromOverflow };
}
